import React, { useState, useMemo, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';

import GenericSelect from '../../../components/ui/GenericSelect';
import TextInput from '../../../components/ui/TextInput.jsx';
import Button from '../../../components/ui/Button';
import CheckboxInput from '../../../components/ui/CheckboxInput';
import {
  useEligibleClosedPeriods, useScaleLevels, useActiveGrades, CYCLE_TYPES,
  buildCycleCreatePayload, isCycleFormValid, gradeRowKey, NEW_CYCLE_INITIAL_FORM,
} from '../config';

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 42, 42, 0.4);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const Modal = styled.div`
  background: #fff;
  border-radius: 6px;
  width: 640px;
  max-width: 92vw;
  max-height: 88vh;
  overflow-y: auto;
  padding: 28px;
  box-shadow: 0 16px 48px rgba(0, 66, 66, 0.15);
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
`;

const Title = styled.h3`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
`;

const CloseBtn = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: none;
  cursor: pointer;
  padding: 6px;
  border-radius: 4px;
  color: #A0A8B4;
  &:hover:not(:disabled) { background: #F1F5F9; color: #004242; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const SectionTitle = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 20px 0 12px;
  &:first-child { margin-top: 0; }
`;

const Field = styled.div`
  margin-bottom: 16px;
`;

const InfoText = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  color: #64748B;
  background: #F1F5F9;
  border-radius: 4px;
  padding: 8px 12px;
  margin-bottom: 16px;
`;

const HintText = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 12px;
  color: #EA580C;
  background: #FFF7ED;
  border-radius: 4px;
  padding: 8px 12px;
  margin-bottom: 16px;
`;

const MatrixTable = styled.div`
  display: grid;
  grid-template-columns: 1.4fr 1fr 1fr 1fr;
  gap: 8px 10px;
  align-items: center;
  margin-bottom: 12px;
`;

const MatrixHeaderCell = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 11px;
  font-weight: 700;
  color: #64748B;
`;

const MatrixLevelLabel = styled.div`
  font-family: 'Nunito Sans', sans-serif;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.text.primary};
`;

// Kademe satırı: seviyenin altında girintili (spec 2026-10-27 §7.7 "Kademeye Göre Ayır").
const MatrixGradeLabel = styled(MatrixLevelLabel)`
  padding-left: 16px;
  color: ${({ theme }) => theme.colors.text.muted};
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 8px;
`;

// Faz G (spec 2026-08-08 §3/§4, Task 4) — Yeni Tur sihirbazı: dönem (kapanmış+
// seviyeli, useEligibleClosedPeriods) + ad + tip (raise|bonus) + matris
// editörü (dönemin skalasından seviye satırları — useScaleLevels, dönem
// seçilince ratingScaleId'den çözülür; NewSessionModal.js'in hedefler
// bölümüyle AYNI desen, min/hedef/maks üç sütunlu).
export default function NewCycleModal({ onClose, onCreate }) {
  const { t } = useTranslation();

  const [form, setForm] = useState(NEW_CYCLE_INITIAL_FORM);
  const [saving, setSaving] = useState(false);

  const { periods, periodsFetching } = useEligibleClosedPeriods();
  const selectedPeriod = useMemo(
    () => periods.find((p) => p.id === form.periodId) || null,
    [periods, form.periodId],
  );
  const { levels, levelsFetching } = useScaleLevels(selectedPeriod?.ratingScaleId);
  const grades = useActiveGrades();

  // Dönem değişince o döneme ait olmayan matris satırları taşınmasın.
  useEffect(() => {
    setForm((f) => ({ ...f, matrix: {} }));
  }, [selectedPeriod?.ratingScaleId]);

  const setField = useCallback((name, value) => {
    setForm((f) => ({ ...f, [name]: value }));
  }, []);

  const setMatrixCell = useCallback((levelId, field, value) => {
    setForm((f) => ({
      ...f,
      matrix: {
        ...f.matrix,
        [levelId]: { ...(f.matrix[levelId] || {}), [field]: value },
      },
    }));
  }, []);

  const periodOptions = useMemo(() => periods.map((p) => ({ id: p.id, name: p.name })), [periods]);
  const typeOptions = useMemo(() => CYCLE_TYPES.map((v) => ({
    id: v, name: t(`compCycles.type.${v}`),
  })), [t]);

  const valid = useMemo(() => isCycleFormValid(form, levels, grades), [form, levels, grades]);
  const sortedLevels = useMemo(
    () => levels.slice().sort((a, b) => a.sortOrder - b.sortOrder),
    [levels],
  );

  const handleSubmit = useCallback(async () => {
    setSaving(true);
    const newId = await onCreate(buildCycleCreatePayload(form, levels, grades));
    if (newId) {
      onClose();
      return;
    }
    setSaving(false);
  }, [onCreate, onClose, form, levels, grades]);

  // Tek matris satırı: genel satırın anahtarı levelId, kademe satırınınki levelId|gradeId.
  const renderRow = (key, label, isGrade) => {
    const row = form.matrix[key] || {};
    const Label = isGrade ? MatrixGradeLabel : MatrixLevelLabel;
    return (
      <React.Fragment key={key}>
        <Label data-row={key}>{label}</Label>
        {['min', 'target', 'max'].map((field) => (
          <TextInput
            key={field}
            type="number"
            min="0"
            max="100"
            placeholder="%"
            value={row[field] ?? ''}
            onChange={(e) => setMatrixCell(key, field, e.target.value)}
          />
        ))}
      </React.Fragment>
    );
  };

  return (
    <Overlay onClick={saving ? undefined : onClose}>
      <Modal onClick={(e) => e.stopPropagation()}>
        <Header>
          <Title>{t('compCycles.wizard.title')}</Title>
          <CloseBtn onClick={onClose} disabled={saving}><X size={18} /></CloseBtn>
        </Header>

        <SectionTitle>{t('compCycles.wizard.basicsSection')}</SectionTitle>
        <Field>
          <GenericSelect
            label={t('compCycles.fields.period')}
            options={periodOptions}
            value={form.periodId}
            onChange={(v) => setField('periodId', v)}
            idKey="id"
            labelKey="name"
            required
          />
          {!periodsFetching && periods.length === 0 && (
            <InfoText>{t('compCycles.wizard.noPeriodsHint')}</InfoText>
          )}
        </Field>
        <Field>
          <TextInput
            label={t('compCycles.fields.name')}
            value={form.name}
            onChange={(e) => setField('name', e.target.value)}
            required
          />
        </Field>
        <Field>
          <GenericSelect
            label={t('compCycles.fields.type')}
            options={typeOptions}
            value={form.cycleType}
            onChange={(v) => setField('cycleType', v)}
            idKey="id"
            labelKey="name"
            required
          />
        </Field>

        {selectedPeriod && (
          <>
            <SectionTitle>{t('compCycles.wizard.matrixSection')}</SectionTitle>
            {!levelsFetching && sortedLevels.length === 0 && (
              <InfoText>{t('compCycles.wizard.noLevelsHint')}</InfoText>
            )}
            {!levelsFetching && sortedLevels.length > 0 && (
              <>
                {grades.length > 0 && (
                  <Field>
                    <CheckboxInput
                      label={t('compCycles.wizard.splitByGrade')}
                      checked={!!form.splitByGrade}
                      onChange={(v) => setField('splitByGrade', v)}
                      data-role="split-by-grade"
                    />
                  </Field>
                )}
                <MatrixTable data-role="guideline-matrix">
                  <MatrixHeaderCell />
                  <MatrixHeaderCell>{t('compCycles.fields.minPct')}</MatrixHeaderCell>
                  <MatrixHeaderCell>{t('compCycles.fields.targetPct')}</MatrixHeaderCell>
                  <MatrixHeaderCell>{t('compCycles.fields.maxPct')}</MatrixHeaderCell>
                  {sortedLevels.map((level) => (
                    <React.Fragment key={level.id}>
                      {renderRow(level.id, level.label, false)}
                      {form.splitByGrade && grades.map((grade) => renderRow(gradeRowKey(level.id, grade.id), grade.name, true))}
                    </React.Fragment>
                  ))}
                </MatrixTable>
                <HintText>{t('compCycles.wizard.matrixHint')}</HintText>
                {form.splitByGrade && <HintText>{t('compCycles.wizard.splitByGradeHint')}</HintText>}
              </>
            )}
          </>
        )}

        <Footer>
          <Button variant="outline" onClick={onClose} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button data-role="create-cycle-btn" onClick={handleSubmit} disabled={!valid || saving}>
            {saving ? t('common.saving') : t('common.save')}
          </Button>
        </Footer>
      </Modal>
    </Overlay>
  );
}
